import { v4 as uuidv4 } from 'uuid';
import type { SupportFullStateMediaSource } from '@/types/supportFullState';

const DIRECTORY_NAME = 'support-full-state-media-v1';
const CAPTURE_DIRECTORY = /^capture-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const activeDirectories = new Set<string>();
const LOCAL_FAILURES = ['local_media_inspect_failed', 'local_media_copy_failed', 'local_media_read_failed', 'local_media_cleanup_failed'] as const;
type LocalFailure = typeof LOCAL_FAILURES[number];
type NativeFile = { uri: string; info(): { exists: boolean; size?: number }; copy(destination: NativeFile): Promise<void> };
type NativeDirectory = {
  uri: string; name: string; info(): { exists: boolean };
  create(options?: { intermediates?: boolean; idempotent?: boolean }): void;
  list(): Array<NativeDirectory | NativeFile>; delete(): void;
};
// O barrel do SDK não expõe os membros herdados nas declarações usadas pelo app.
type NativeFileSystem = {
  File: new (...uris: Array<string | NativeDirectory>) => NativeFile;
  Directory: new (...uris: Array<string | NativeDirectory>) => NativeDirectory;
  Paths: { document: NativeDirectory };
};

export const supportMediaLocalFailure = (reason: LocalFailure): Error => new Error(`support_full_state_${reason}`);
export const isSupportMediaLocalFailure = (error: unknown): boolean => error instanceof Error &&
  LOCAL_FAILURES.some(reason => error.message === `support_full_state_${reason}`);
export const isSupportMediaSnapshotUri = (uri: string): boolean => uri.includes(`/${DIRECTORY_NAME}/`);

export interface NativeSupportMediaSnapshot {
  readUri(sourceUri: string): string | null;
  readonly fileCount: number;
  readonly byteLength: number;
  dispose(): void;
}

/** As cópias pertencem somente a esta captura; nunca entram no banco ou na fila de mídia. */
export const prepareNativeSupportMediaSnapshot = async (
  media: readonly SupportFullStateMediaSource[],
  onCleanupFailure: (error: Error) => void,
): Promise<NativeSupportMediaSnapshot> => {
  const fileSystem = require('expo-file-system') as NativeFileSystem;
  const readUris = new Map<string, string | null>();
  let directory: NativeDirectory | null = null;
  let disposed = false;
  let fileCount = 0;
  let byteLength = 0;
  const dispose = () => {
    if (disposed) return;
    disposed = true;
    try {
      if (directory?.info().exists) directory.delete();
    } catch { throw supportMediaLocalFailure('local_media_cleanup_failed'); }
    finally { if (directory) activeDirectories.delete(directory.uri); }
  };
  try {
    try {
      // Document mantém a cópia disponível durante a coleta, inclusive sob pressão no cache.
      const root = new fileSystem.Directory(fileSystem.Paths.document, DIRECTORY_NAME);
      root.create({ intermediates: true, idempotent: true });
      directory = new fileSystem.Directory(root, `capture-${uuidv4()}`);
      activeDirectories.add(directory.uri);
      // Após reabrir o processo, somente diretórios próprios sem captura ativa são órfãos.
      try {
        for (const entry of root.list()) {
          if (entry instanceof fileSystem.Directory && CAPTURE_DIRECTORY.test(entry.name) && !activeDirectories.has(entry.uri)) entry.delete();
        }
      } catch { throw supportMediaLocalFailure('local_media_cleanup_failed'); }
      directory.create();
    } catch (error) {
      if (isSupportMediaLocalFailure(error)) throw error;
      throw supportMediaLocalFailure('local_media_copy_failed');
    }
    for (const source of media) {
      if (isSupportMediaSnapshotUri(source.sourceUri) || readUris.has(source.sourceUri)) continue;
      let readableUri = source.sourceUri;
      if (/^checklist-(photos|signatures|marcacoes|marcacao-bkg)\//.test(readableUri)) {
        readableUri = `${fileSystem.Paths.document.uri.replace(/\/$/, '')}/${readableUri}`;
      }
      if (!/^(file|content):\/\//.test(readableUri)) continue;
      let file: NativeFile;
      let info: ReturnType<typeof file.info>;
      try {
        file = new fileSystem.File(readableUri);
        info = file.info();
      } catch { throw supportMediaLocalFailure('local_media_inspect_failed'); }
      if (!info.exists) { readUris.set(source.sourceUri, null); continue; }
      try {
        const copy = new fileSystem.File(directory, `${readUris.size}.bin`);
        await file.copy(copy);
        const copied = copy.info();
        const copiedSize = copied.size;
        if (!copied.exists || typeof copiedSize !== 'number' || !Number.isFinite(copiedSize) || copiedSize < 0 ||
          (typeof info.size === 'number' && copiedSize !== info.size)) throw supportMediaLocalFailure('local_media_copy_failed');
        readUris.set(source.sourceUri, copy.uri);
        fileCount += 1;
        byteLength += copiedSize;
      } catch { throw supportMediaLocalFailure('local_media_copy_failed'); }
    }
    return { readUri: uri => readUris.has(uri) ? readUris.get(uri)! : uri, fileCount, byteLength, dispose };
  } catch (error) {
    try { dispose(); } catch { onCleanupFailure(supportMediaLocalFailure('local_media_cleanup_failed')); }
    throw error;
  }
};
